import type { PopoverProps } from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import type { ReactNode, Ref } from 'react';

import { Popover, Portal } from '@frontend/components';

import { FindingPopoverBody } from './FindingPopoverBody';
import { FindingPopoverTrigger } from './FindingPopoverTrigger';
import type { FindingData } from './types';

export type FindingPopoverProps = {
  finding: FindingData;
  isInPreview?: boolean;
  isHighlighted?: boolean;
  trigger?: ReactNode;
  triggerProps?: Record<string, unknown>;
  triggerRef?: Ref<HTMLButtonElement>;
  contentRef?: Ref<HTMLDivElement>;
  /** Slot for top-right icon buttons (copy, create-rule icon, etc.) */
  headerActions?: ReactNode;
  /** Slot for body actions area (geometric set-as, part descriptor set-as, etc.) */
  actionSlot?: ReactNode;
  /** Slot for footer actions area (mark inaccurate, create rule, learn more, etc.) */
  footerSlot?: ReactNode;
  additionalContent?: ReactNode;
  currentPartFileUuid?: string | null;
  showProvenance?: boolean;
} & Omit<PopoverProps, 'trigger'>;

export const FindingPopover = ({
  finding,
  isInPreview = false,
  isHighlighted = false,
  trigger,
  triggerProps,
  triggerRef,
  contentRef,
  headerActions,
  actionSlot,
  footerSlot,
  additionalContent,
  currentPartFileUuid,
  showProvenance,
  isOpen: isOpenProp,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  defaultIsOpen,
  ...popoverProps
}: FindingPopoverProps) => {
  const disclosure = useDisclosure({ defaultIsOpen });
  const isOpen = isOpenProp ?? disclosure.isOpen;
  const onOpen = onOpenProp ?? disclosure.onOpen;
  const onClose = onCloseProp ?? disclosure.onClose;

  return (
    <Popover
      isLazy
      returnFocusOnClose={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      {...popoverProps}
    >
      {trigger ?? (
        <FindingPopoverTrigger
          ref={triggerRef}
          finding={finding}
          isInPreview={isInPreview}
          isHighlighted={isHighlighted}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          {...triggerProps}
        />
      )}
      {isOpen && (
        <Portal overrideOverlayContainer>
          <FindingPopoverBody
            finding={finding}
            headerActions={headerActions}
            actionSlot={actionSlot}
            footerSlot={footerSlot}
            additionalContent={additionalContent}
            contentRef={contentRef}
            currentPartFileUuid={currentPartFileUuid}
            showProvenance={showProvenance}
          />
        </Portal>
      )}
    </Popover>
  );
};
FindingPopover.displayName = 'FindingPopover';
